import { Link } from 'react-router-dom';
import {
  Smartphone, BatteryCharging, Usb, Fingerprint, Cpu, CheckCircle2, ShieldCheck, Zap, Clock, MapPin, Star,
} from 'lucide-react';
import Seo from '../../components/Seo';
import ServicePageLayout from '../../components/services/ServicePageLayout';
import { ServiceSection, ServiceFAQ, RelatedServices } from '../../components/services/ServiceUI';
import {
  ServiceHeroPro, ProofStrip, DiagnosisList, PriceCardPro, AvisStrip, LocalCTA,
} from '../../components/services/elevated';
import { REVIEWS, RATING, REVIEW_COUNT } from '../../data/reviews';
import DevisSection from '../../components/DevisSection';

const SLUG = '/reparation-samsung-montpellier';

const faq = [
  { q: "Réparez-vous les écrans AMOLED Samsung ?", a: "Oui, nous remplaçons les dalles AMOLED de toutes les gammes Galaxy, vitre et affichage compris." },
  { q: "Faut-il un rendez-vous ?", a: "Non, présentez-vous directement en boutique, du lundi au samedi de 10h à 20h." },
  { q: "Réparez-vous les Galaxy pliables (Z Fold / Z Flip) ?", a: "Oui, ces modèles sont pris en charge ; le diagnostic et le devis sont gratuits." },
  { q: "Quelle est la durée d'une réparation Samsung ?", a: "La plupart des réparations sont réalisées en 30 à 45 minutes." },
];

const pannes = [
  { icon: Smartphone, title: "Écran AMOLED / vitre cassée", desc: (<>Voir notre page <Link to="/reparation-ecran-samsung-montpellier" className="text-blue-600 font-medium hover:underline">réparation écran Samsung</Link>.</>) },
  { icon: BatteryCharging, title: "Batterie usée", desc: "Se décharge vite ou gonfle — remplacement." },
  { icon: Usb, title: "Port de charge USB-C", desc: "Port abîmé, charge intermittente." },
  { icon: Fingerprint, title: "Photo, boutons, empreinte", desc: "Appareil photo, haut-parleur, lecteur d'empreinte." },
  { icon: Cpu, title: "Eau & carte mère", desc: (<>Dégât des eaux et pannes de carte mère (<Link to="/micro-soudure-montpellier" className="text-blue-600 font-medium hover:underline">micro-soudure</Link>).</>) },
];

const reasons = [
  "Centre-ville de Montpellier, sans rendez-vous, du lundi au samedi 10h–20h.",
  "Devis gratuit avant toute intervention.",
  "Garantie 6 mois sur les réparations.",
  "Maîtrise des dalles AMOLED et de la micro-soudure.",
];

const proofItems = [
  { icon: Zap, label: 'Réparé en 30–45 min' },
  { icon: ShieldCheck, label: 'Garantie 6 mois' },
  { icon: Clock, label: 'Sans rendez-vous' },
  { icon: Star, label: '4,7/5 · 579 avis' },
  { icon: MapPin, label: 'Centre-ville Montpellier' },
];

const ReparationSamsung = () => (
  <ServicePageLayout trail={[{ name: 'Accueil', to: '/' }, { name: 'Réparation Samsung Galaxy' }]}>
    <Seo
      title="Réparation Samsung Galaxy Montpellier | AlloRéparations"
      description="Réparation Samsung Galaxy à Montpellier : écran, batterie, connecteur. Toutes gammes S, A, Note, Z. Devis gratuit, garantie 6 mois."
      canonicalPath={SLUG}
      service={{
        name: "Réparation Samsung Galaxy à Montpellier",
        serviceType: "Réparation de smartphone Samsung Galaxy",
        description: "Réparation Samsung Galaxy à Montpellier : écran AMOLED, batterie, port de charge USB-C, micro-soudure. Gammes S, A, Note et Z.",
      }}
      faq={faq}
      breadcrumb={[
        { name: 'Accueil', path: '/' },
        { name: 'Réparation Samsung Galaxy à Montpellier', path: SLUG },
      ]}
    />

    <ServiceHeroPro
      eyebrow="Samsung Galaxy — Montpellier"
      h1Before="Réparation "
      h1Highlight="Samsung Galaxy"
      h1After=" à Montpellier"
      intro="AlloRéparations répare votre smartphone Samsung Galaxy à Montpellier centre-ville, sans rendez-vous et avec devis gratuit. Écran AMOLED cassé, batterie en fin de vie, port de charge HS ou téléphone qui ne s'allume plus : nous intervenons sur toutes les gammes Galaxy S, A, Note et Z, avec une garantie de 6 mois."
      image={{ src: '/iphones/samsung-galaxy.png', alt: "Réparation Samsung Galaxy à Montpellier — écran AMOLED et batterie" }}
      rating={{ value: RATING, count: REVIEW_COUNT }}
      hasWidget
    />

    <ProofStrip items={proofItems} />

    <DiagnosisList title="Les pannes Samsung que nous réparons" items={pannes} />

    <ServiceSection title="Toutes les gammes Galaxy">
      <p>
        Nous réparons les Galaxy S (S20 à S24 et antérieurs), Galaxy A (A12, A52, A54…), Galaxy Note, ainsi que
        les pliables Galaxy Z Fold et Z Flip. Les pièces des modèles les plus courants sont disponibles pour une
        réparation rapide.
      </p>
    </ServiceSection>

    <ServiceSection title="Pourquoi nous confier votre Samsung">
      <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
        {reasons.map((r, i) => (
          <div key={i} className="flex items-start gap-3">
            <CheckCircle2 size={20} className="text-emerald-500 flex-shrink-0 mt-0.5" />
            <span className="text-slate-700">{r}</span>
          </div>
        ))}
      </div>
    </ServiceSection>

    <PriceCardPro
      eyebrow="Tarifs de référence"
      title="Tarifs de réparation Samsung à Montpellier"
      columns={['Réparation', 'Délai', 'Prix TTC']}
      rows={[
        ['Écran Galaxy série A', '30–45 min', 'dès 120 €'],
        ['Écran Galaxy série S / Note', '45 min', 'dès 180 €'],
        ['Changement de batterie', '30–45 min', 'dès 65 €'],
        ['Port de charge USB-C', '45 min', 'sur devis'],
      ]}
    />

    <ServiceSection title="Estimez votre réparation ou le rachat de votre iPhone">
      <p>
        Notre configurateur de devis instantané couvre la gamme iPhone et le rachat. Pour un devis Samsung précis
        (modèle et type de panne), appelez-nous au <a href="tel:+33983820754" className="text-blue-600 font-medium hover:underline">09&nbsp;83&nbsp;82&nbsp;07&nbsp;54</a> ou
        passez en boutique : le diagnostic est gratuit.
      </p>
    </ServiceSection>
    <DevisSection />

    <AvisStrip rating={RATING} count={REVIEW_COUNT} reviews={REVIEWS.slice(0, 3)} />

    <ServiceFAQ faq={faq} />

    <LocalCTA heading="Un Galaxy à réparer ? Diagnostic gratuit en boutique." sub="Écran AMOLED, batterie, charge — sans rendez-vous, garantie 6 mois." />

    <RelatedServices
      links={[
        { label: "Réparation d'écran Samsung Galaxy", to: '/reparation-ecran-samsung-montpellier', desc: 'Dalle AMOLED, vitre, tactile.' },
        { label: 'Micro-soudure & carte mère', to: '/micro-soudure-montpellier', desc: "Ne s'allume plus, charge HS." },
      ]}
    />
  </ServicePageLayout>
);

export default ReparationSamsung;
